/**
 * PaymentAnalytics slice — payment-status counts for the payments correlated
 * with an artifact or execution.
 *
 * fetchPaymentAnalytics({ correlationTags, region }): POST /payments/analytics
 * via beaconApi, region-pinned (the operation is deployed in every region). The
 * artifact and execution detail pages pass their entity's correlationTags; the
 * PaymentAnalytics component renders the resulting totalPayments + statusCounts.
 */
import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';
import { GetPaymentAnalyticsOutput } from './paymentAnalyticsApi';
import { beaconApi } from '../../../services/beaconApi';
import { RootState } from '../../store';
import { resolveApiBase } from '../env/envState';

export interface PaymentAnalyticsState {
  correlationTags: string[];
  totalPayments: number;
  /** paymentStatus name -> count (every status, zero-filled). */
  statusCounts: Record<string, number>;
  loading: boolean;
  error: string | null;
  currentRequestId: string | null;
}

export const paymentAnalyticsInitialState: PaymentAnalyticsState = {
  correlationTags: [],
  totalPayments: 0,
  statusCounts: {},
  loading: false,
  error: null,
  currentRequestId: null,
};

export const fetchPaymentAnalytics = createAsyncThunk<
  GetPaymentAnalyticsOutput, { correlationTags: string[]; region: string }, { state: RootState }
>('paymentAnalytics/fetch', async ({ correlationTags, region }, { getState }) => {
  const apiBase = resolveApiBase(getState().env.stage, region);
  return beaconApi.getPaymentAnalytics({ correlationTags }, apiBase);
});

const paymentAnalyticsSlice = createSlice({
  name: 'paymentAnalytics',
  initialState: paymentAnalyticsInitialState,
  reducers: {
    clearPaymentAnalytics: () => paymentAnalyticsInitialState,
  },
  extraReducers: (builder) => {
    builder
      .addCase(fetchPaymentAnalytics.pending, (state, action) => {
        state.loading = true;
        state.error = null;
        state.currentRequestId = action.meta.requestId;
      })
      .addCase(fetchPaymentAnalytics.fulfilled, (state, action) => {
        if (action.meta.requestId !== state.currentRequestId) return;
        state.loading = false;
        state.correlationTags = action.payload.correlationTags;
        state.totalPayments = action.payload.totalPayments;
        state.statusCounts = action.payload.statusCounts;
      })
      .addCase(fetchPaymentAnalytics.rejected, (state, action) => {
        if (action.meta.requestId !== state.currentRequestId) return;
        state.loading = false;
        state.error = action.error.message || 'Failed to load payment analytics';
      });
  },
});

export const { clearPaymentAnalytics } = paymentAnalyticsSlice.actions;
export default paymentAnalyticsSlice.reducer;
